import React, { useId } from 'react';

/**
 * Sparkline - Mini chart component for showing stock price trends
 * 
 * Props:
 * - data: Array of numbers representing price points
 * - color: 'green' | 'red' | 'auto' - auto determines based on trend
 * - width: Chart width (default: 80)
 * - height: Chart height (default: 32)
 */
function Sparkline({ data, color = 'auto', width = 80, height = 32 }) {
  const uniqueId = useId();
  
  if (!data || data.length < 2) {
    return null;
  }

  // Determine color based on trend if auto, otherwise use provided color
  const trend = data[data.length - 1] - data[0];
  let lineColor;
  
  if (color === 'auto') {
    lineColor = trend >= 0 ? '#10b981' : '#ef4444';
  } else if (color === 'green') {
    lineColor = '#10b981';
  } else if (color === 'red') {
    lineColor = '#ef4444';
  } else {
    // Accept hex color directly
    lineColor = color;
  }

  // Calculate chart dimensions
  const padding = 2;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  // Normalize data to fit chart
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  // Generate path points
  const points = data.map((value, index) => {
    const x = padding + (index / (data.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((value - min) / range) * chartHeight;
    return `${x},${y}`;
  });

  const pathD = `M ${points.join(' L ')}`;

  // Generate area fill path
  const areaD = `${pathD} L ${padding + chartWidth},${padding + chartHeight} L ${padding},${padding + chartHeight} Z`;

  const gradientId = `sparkline-gradient-${uniqueId}`;

  return (
    <svg 
      width={width} 
      height={height} 
      viewBox={`0 0 ${width} ${height}`}
      className="sparkline"
    >
      {/* Gradient fill */}
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.3" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      
      {/* Area fill */}
      <path
        d={areaD}
        fill={`url(#${gradientId})`}
      />
      
      {/* Line */}
      <path
        d={pathD}
        fill="none"
        stroke={lineColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Generate mock sparkline data based on stock performance
 * In production, this would come from real historical data
 */
export function generateSparklineData(changePercent, points = 20) {
  const data = [];
  const baseValue = 100;
  
  // Handle null/undefined changePercent
  const change = changePercent || 0;
  const volatility = Math.max(Math.abs(change) * 0.3, 0.5);
  
  // Start value and target end value
  const startValue = baseValue;
  const endValue = baseValue * (1 + change / 100);
  
  // Generate path from start to end with some noise
  for (let i = 0; i < points; i++) {
    const progress = i / (points - 1);
    // Linear interpolation from start to end
    const trendValue = startValue + (endValue - startValue) * progress;
    // Add randomness that decreases towards the end
    const noise = (Math.random() - 0.5) * volatility * (1 - progress * 0.5);
    data.push(trendValue + noise);
  }
  
  // Ensure first and last points are exact
  data[0] = startValue;
  data[data.length - 1] = endValue;
  
  return data;
}

export default Sparkline;